import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLang } from '../context/LangContext';
import { FATWA_API, STORAGE_BASE, cachedFetch, SCHOLAR_COLORS, scholarInitial, extractCvBio, fatwaNum, trunc, scholarTitledName } from '../utils/api';

// Build a usable URL for a researcher photo path (handles full URLs and storage paths)
function photoUrl(photo) {
  if (!photo) return '';
  return photo.startsWith('http') ? photo : STORAGE_BASE + photo;
}

function catName(c, lang) {
  if (!c) return '';
  if (lang === 'ar') return c.name_ar || '';
  if (c.translations && c.translations[lang] && c.translations[lang].name) {
    return c.translations[lang].name;
  }
  if (lang === 'en') return c.name_en || c.name_ar || '';
  return c.name_ar || '';
}
function fatwaCatName(f, categories, lang, fallback = '') {
  if (!f) return fallback;
  const cat = (categories || []).find(c =>
    c.id === f.category_id ||
    c.name_ar === f.category_name ||
    c.name_en === f.category_name
  );
  if (cat) return catName(cat, lang);
  return f.category_name || fallback;
}

export function ScholarsPage({ scholars }) {
  const { t, lang } = useLang();
  const navigate = useNavigate();

  return (
    <div id="page-scholars">
      <div className="page-hero">
        <div className="geo-lg"/><div className="page-hero-glow"/>
        <div className="pw">
          <div className="breadcrumb">
            <a href="/" onClick={e=>{e.preventDefault();navigate('/');}}>
              {t('nav.home.label')}
            </a>
            <span>/</span><span>{t('sch.title1')} {t('sch.title2')}</span>
          </div>
          <h1>{t('sch.title1')} <em>{t('sch.title2')}</em></h1>
          <p>{t('sch.subtitle')}</p>
        </div>
      </div>
      <div className="pw">
        <div className="scholars-grid">
          {scholars.length===0 ? (
            <div className="scholar-card-full" style={{pointerEvents:'none'}}>
              <div className="scf-left" style={{background:SCHOLAR_COLORS[0]}}>
                <div className="geo-sm"/>
                <div className="scf-avatar" style={{background:'rgba(255,255,255,.15)'}}/>
              </div>
              <div className="scf-right">
                <div className="skel skel-tag" style={{width:200}}/>
                <div className="skel skel-line" style={{width:'85%',marginTop:'.6rem'}}/>
              </div>
            </div>
          ) : scholars.map((s,i)=>{
            const bg=SCHOLAR_COLORS[i%SCHOLAR_COLORS.length];
            const bio=extractCvBio(s,180);
            return(
              <div key={s.id} className="scholar-card-full" onClick={()=>navigate(`/scholars/${s.id}`)} style={{cursor:'pointer'}}>
                <div className="scf-left" style={{background:bg}}>
                  <div className="geo-sm"/>
                  <div className="scf-avatar">
                    {photoUrl(s.photo)
                      ? <img src={photoUrl(s.photo)} alt={s.name} style={{width:'100%',height:'100%',objectFit:'cover',borderRadius:'inherit'}} />
                      : scholarInitial(s.name)}
                  </div>
                  <div className="name">{scholarTitledName(s.name, lang)}</div>
                </div>
                <div className="scf-right">
                  <div className="scf-spec">{s.role||t('label.scholar.role')}</div>
                  <p className="scf-bio">{bio||t('label.scholar.bio')}</p>
                  <button className="scf-btn" onClick={e=>{e.stopPropagation();navigate(`/scholars/${s.id}`);}}>
                    {t('schsin.profile')}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export function SingleScholarPage({ scholar, scholars, categories, lang }) {
  const { t } = useLang();
  const navigate = useNavigate();
  const [fatwas, setFatwas] = useState([]);

  const idx=scholars.indexOf(scholar);
  const bg=SCHOLAR_COLORS[(idx>=0?idx:0)%SCHOLAR_COLORS.length];

  useEffect(()=>{
    if(!scholar)return;
    setFatwas([]);
    cachedFetch(`${FATWA_API}?page=1&language=${lang}&search=${encodeURIComponent(scholar.name)}`)
      .then(json=>{
        const data=(json.data||[]).filter(f=>(f.scholar||'').trim()===scholar.name).slice(0,4);
        setFatwas(data);
      }).catch(()=>{});
  },[scholar,lang]);

  if(!scholar)return null;

  const skip=['الرئيسية','التجاوز','تبديل','اتصل','روابط','للتبرع','Stripe','Square','English','اردو','© 20','www.'];
  // Images that can be dropped into the bio text via [image-1], [image-2]… markers.
  const contentImages=Array.isArray(scholar.content_images)?scholar.content_images:[];
  const keepText=l=>l.length>10&&!skip.some(s=>l.includes(s));
  // Walk the CV text line by line and turn it into an ordered list of blocks
  // (text paragraphs + images), so an [image-N] marker renders the matching
  // image right where it sits in the text.
  const cvBlocks=[];
  const usedImg=new Set();
  (scholar.cv_full_text||'').split('\n').forEach(raw=>{
    const line=raw.trim();
    if(!line)return;
    const re=/\[image[-\s:]*(\d+)\]/ig;
    if(!re.test(line)){ if(keepText(line)) cvBlocks.push({type:'text',value:line}); return; }
    re.lastIndex=0; let last=0,m;
    while((m=re.exec(line))!==null){
      const before=line.slice(last,m.index).trim();
      if(keepText(before)) cvBlocks.push({type:'text',value:before});
      const idx=parseInt(m[1],10)-1;
      if(contentImages[idx]){ cvBlocks.push({type:'image',value:contentImages[idx]}); usedImg.add(idx); }
      last=m.index+m[0].length;
    }
    const after=line.slice(last).trim();
    if(keepText(after)) cvBlocks.push({type:'text',value:after});
  });
  // Fallback: any uploaded image not positioned with an [image-N] token is
  // appended at the end, so images always show up once added in the admin.
  contentImages.forEach((img,idx)=>{ if(!usedImg.has(idx)) cvBlocks.push({type:'image',value:img}); });
  // Group runs of consecutive images into a gallery so they sit 3-per-row
  // instead of stacking one under another.
  const cvGroups=[];
  cvBlocks.forEach(b=>{
    const last=cvGroups[cvGroups.length-1];
    if(b.type==='image'&&last&&last.type==='gallery') last.images.push(b.value);
    else if(b.type==='image') cvGroups.push({type:'gallery',images:[b.value]});
    else cvGroups.push(b);
  });

  return(
    <div id="page-scholar-single">
      <div className="page-hero" style={{minHeight:260,background:bg}}>
        <div className="geo-lg"/><div className="page-hero-glow"/>
        <div className="pw">
          <div className="breadcrumb">
            <a href="/" onClick={e=>{e.preventDefault();navigate('/');}}>
              {t('nav.home.label')}
            </a>
            <span>/</span>
            <a href="/scholars" onClick={e=>{e.preventDefault();navigate('/scholars');}}>
              {t('sch.title1')} {t('sch.title2')}
            </a>
            <span>/</span><span>{scholar.name}</span>
          </div>
          <h1>{scholar.name.split(' ').slice(0,-1).join(' ')} <em>{scholar.name.split(' ').slice(-1)}</em></h1>
          <p>{scholar.role||''}</p>
        </div>
      </div>
      <div className="pw">
        <div className="single-grid" style={{padding:'1.5rem 0'}}>
          <div>
            <div className="about-card">
              <h2>{t('schsin.bio.title')}</h2>
              {cvGroups.length>0
                ?cvGroups.map((b,i)=>b.type==='gallery'
                  ?(b.images.length===1
                    ?<figure key={i} style={{margin:'1.3rem 0'}}>
                       <img src={photoUrl(b.images[0])} alt="" loading="lazy"
                            style={{maxWidth:'100%',height:'auto',borderRadius:'12px',display:'block'}} />
                     </figure>
                    :<div key={i} style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:'10px',margin:'1.3rem 0'}}>
                       {b.images.map((img,j)=>
                         <img key={j} src={photoUrl(img)} alt="" loading="lazy"
                              style={{width:'100%',aspectRatio:'4 / 3',objectFit:'cover',borderRadius:'12px',display:'block'}} />)}
                     </div>)
                  :<p key={i} style={{color:'var(--ink2)',lineHeight:1.9,marginBottom:'.7rem'}}>{b.value}</p>)
                :<p>{t('label.scholar.bio')}</p>
              }
            </div>
          </div>
          <aside>
            <div className="single-sbox" style={{textAlign:'center'}}>
              <div className="ssb-head">👤 {scholar.name}</div>
              <div style={{padding:'1.5rem'}}>
                <div style={{width:80,height:80,margin:'0 auto 1rem',background:bg,borderRadius:'50%',overflow:'hidden',display:'flex',alignItems:'center',justifyContent:'center',fontFamily:'var(--fam-display)',fontSize:'2.2rem',color:'var(--a4)'}}>
                  {photoUrl(scholar.photo)
                    ? <img src={photoUrl(scholar.photo)} alt={scholar.name} style={{width:'100%',height:'100%',objectFit:'cover'}} />
                    : scholarInitial(scholar.name)}
                </div>
                <div style={{fontSize:'.78rem',color:'var(--ink3)',lineHeight:1.8}}>
                  <div>🎓 {scholar.role||t('label.scholar.role')}</div>
                </div>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}